import React, { useState } from 'react'

import { Context } from 'types/GlobalUserEvents'
import { MetricStatus, TMetricSettings } from 'types/GlobalMetric'
import { SxProps } from '@mui/material'
import { TKeyMetric } from 'types/GlobalKeyMetric'

import { useMetric } from 'redux/hooks/Metrics'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Link from 'components_new/atoms/Link'
import Skeleton from 'components_new/atoms/Skeleton'
import Text from 'components_new/atoms/Text'

import InfoButton from 'components_new/molecules/InfoButton'

import MetricInfoDialog from 'components_new/organisms/dialogs/MetricInfoDialog'

import { addRef } from 'utils/queryHandler'
import { getMetricLinkSearch, getMetricTitle } from 'utils/metric'

export interface MetricTitleProps {
  context?: Context
  disableIcon?: boolean
  id: string
  keyMetric?: TKeyMetric
  link?: boolean
  settings: TMetricSettings
  size?: 'small' | 'medium'
  sx?: SxProps
  titleOverride?: string
}

const MetricTitle = React.memo((props: MetricTitleProps) => {
  const { context, id, keyMetric, link, settings, size, sx, titleOverride } =
    props

  const { lang, t, translateEnum } = useTranslation()

  const metric = useMetric(id, settings.variant_kpi_template_id)

  const [dialog, setDialog] = useState<boolean>(false)

  // info
  const handleClickInfo = () => {
    setDialog(true)
  }

  // close dialog
  const handleCloseDialog = () => {
    setDialog(false)
  }

  if (!metric) return <Skeleton width={'20%'} variant="text" />

  const title = titleOverride
    ? titleOverride
    : keyMetric
      ? keyMetric.title
      : getMetricTitle(metric, settings, lang, translateEnum)

  const href = keyMetric
    ? `/key-metrics/${keyMetric.id}`
    : `/metrics/${metric.id}`

  const navLink = context ? addRef(href, context) : href

  const navSearch = getMetricLinkSearch(settings)

  return (
    <>
      {size === 'small' ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 0.25,
            minWidth: 0,
            ...sx
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {link ? (
              <Link
                component="span"
                href={navLink}
                search={navSearch}
                variant="navigation"
              >
                {title}
              </Link>
            ) : (
              <Text component="span" variant="navigation">
                {title}
              </Text>
            )}
          </Box>

          <InfoButton
            className="MetricTitleInfoButton"
            onClick={(e) => {
              e.stopPropagation()
              handleClickInfo()
            }}
            onMouseDown={(e) => e.stopPropagation()}
            title={t('global', 'more_information')}
          />
        </Box>
      ) : (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 0.5,
            minWidth: 0,
            position: 'relative',
            ...sx
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {link ? (
              <Link
                component="h1"
                href={navLink}
                search={navSearch}
                variant="h5"
              >
                {title}
              </Link>
            ) : (
              <Text component="h1" variant="h5">
                {title}
              </Text>
            )}
          </Box>

          {metric.status === MetricStatus.INACTIVE || metric.access.private ? (
            <Icon
              color="disabled"
              name={
                metric.status === MetricStatus.INACTIVE
                  ? 'VisibilityOffOutlined'
                  : 'Private'
              }
            />
          ) : undefined}
          <InfoButton
            onClick={handleClickInfo}
            title={t('global', 'more_information')}
          />
        </Box>
      )}

      {/*-- info --*/}
      <MetricInfoDialog
        metric={{
          id: id,
          settings: settings
        }}
        onClose={handleCloseDialog}
        open={dialog}
        title={title}
      />
    </>
  )
})

export default MetricTitle
